import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import MobileNav from '../components/MobileNav';
import BrandLogo from '../components/BrandLogo';
import PendingReviewsCard from '../components/PendingReviewsCard';
import CaptainPackageBookingsCard from '../components/CaptainPackageBookingsCard';
import { requestPermissionWithRationale } from '../components/PermissionRationaleModal';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Calendar, MapPin, Users, UserPlus, CheckCircle, XCircle, Crosshair, Trash } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-700',
  present: 'bg-green-100 text-green-700',
  absent: 'bg-red-100 text-red-700',
  replaced: 'bg-gray-200 text-text-secondary',
};

const CaptainDashboard = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/captain/events`, { withCredentials: true });
      setEvents(data || []);
    } catch (err) {
      toast.error('Failed to load events');
    }
    setLoading(false);
  };

  const setVenueLocation = async (eventId) => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported on this device');
      return;
    }
    const granted = await requestPermissionWithRationale('location', { purpose: 'set-venue' });
    if (!granted) return;
    toast.info('Fetching your current location...');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const placeName = window.prompt('Venue / place name (e.g. Sri Mahal, Coimbatore):', '') || '';
        try {
          await axios.post(
            `${BACKEND_URL}/api/captain/events/${eventId}/location`,
            { lat: pos.coords.latitude, lng: pos.coords.longitude, place_name: placeName },
            { withCredentials: true },
          );
          toast.success('Venue location set');
          fetchEvents();
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to set location');
        }
      },
      (err) => toast.error(`Location error: ${err.message}`),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const assignPro = async (eventId) => {
    const proId = window.prompt("Professional/Vendor user ID:");
    if (!proId) return;
    const proName = window.prompt("Their name (display label):") || '';
    const category = window.prompt("Category (e.g. photographer, priest, dj):") || '';
    const expectedTime = window.prompt('Expected check-in (e.g. 2026-09-15T16:00):') || new Date().toISOString();
    try {
      await axios.post(
        `${BACKEND_URL}/api/captain/events/${eventId}/assign-pro`,
        { pro_id: proId, pro_name: proName, category, expected_check_in: expectedTime },
        { withCredentials: true },
      );
      toast.success('Professional assigned');
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign');
    }
  };

  const markAttendance = async (eventId, slotId, status) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/captain/events/${eventId}/attendance`,
        { pro_slot_id: slotId, status },
        { withCredentials: true },
      );
      toast.success(`Marked ${status}`);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const removeSlot = async (eventId, slotId) => {
    if (!window.confirm('Remove this slot?')) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/captain/events/${eventId}/pro/${slotId}`,
        { withCredentials: true },
      );
      toast.success('Slot removed');
      fetchEvents();
    } catch {}
  };

  const markBookingComplete = async (bookingId) => {
    if (!window.confirm('Mark this event as complete? This will open the 7-day window for customer and you to rate the vendors/professionals.')) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/captain/package-bookings/${bookingId}/mark-complete`,
        {},
        { withCredentials: true },
      );
      toast.success('Event marked complete. You and the customer can now rate the vendors.');
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark complete');
    }
  };

  return (
    <div className="min-h-screen bg-sand-50">
      <header className="glass-header sticky top-0 z-50 bg-sand-50/80 border-b border-[#E8E1D5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-5 flex justify-between items-center">
          <BrandLogo size="md" linkTo="/" suffix="Captain" />
          <MobileNav
            links={[]}
            actions={[
              { onClick: logout, label: t('common.logout') || 'Logout', className: 'text-sm tracking-[0.2em] uppercase font-bold text-text-secondary hover:text-text-primary transition-colors', testId: 'header-logout-btn' },
            ]}
          />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-light leading-none text-text-primary mb-2">
            Hi, {user?.name}
          </h1>
          <p className="text-base text-text-secondary">Captain — manage event attendance &amp; replacements</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-text-secondary">Loading events…</div>
        ) : events.length === 0 ? (
          <Card className="bg-white border-[#E8E1D5]">
            <CardContent className="py-12 text-center">
              <p className="text-base text-text-secondary">No events assigned yet. You'll see new events here when admin confirms a customer's package booking.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {events.map((ev) => {
              const isExpanded = expanded === ev.id;
              const totalSlots = (ev.pro_slots || []).length;
              const presentCount = (ev.pro_slots || []).filter(s => s.status === 'present').length;
              return (
                <Card key={ev.id} className="bg-white border-[#E8E1D5]" data-testid={`captain-event-${ev.id}`}>
                  <CardHeader>
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                      <div>
                        <CardTitle className="font-heading text-2xl">{ev.package_name || 'Event'}</CardTitle>
                        <CardDescription className="capitalize mt-1 flex flex-wrap items-center gap-2">
                          <Calendar size={14} /> {ev.event_date} • {ev.event_type} • <Users size={14} /> {ev.guests} guests
                        </CardDescription>
                        <div className="text-sm text-text-secondary mt-1">
                          <strong className="text-text-primary">{ev.customer_name}</strong> • {ev.customer_phone}
                          {ev.event_location_text && <> • <MapPin size={12} className="inline" /> {ev.event_location_text}</>}
                        </div>
                      </div>
                      <div className="flex flex-col items-start md:items-end gap-1 flex-shrink-0">
                        <span className="text-[10px] px-2 py-0.5 rounded font-bold uppercase bg-blue-100 text-blue-700">{ev.status}</span>
                        <span className="text-xs text-text-secondary">{presentCount}/{totalSlots} present</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Venue location */}
                    <div className="flex items-center justify-between p-3 mb-3 border border-[#E8E1D5] rounded bg-sand-50/50">
                      <div className="flex items-center gap-2 min-w-0">
                        <Crosshair size={18} className="text-brand-primary flex-shrink-0" />
                        {ev.venue ? (
                          <div className="text-sm min-w-0">
                            <div className="font-bold text-text-primary truncate">{ev.venue.place_name || 'Venue set'}</div>
                            <div className="text-xs text-text-secondary">{ev.venue.lat.toFixed(5)}, {ev.venue.lng.toFixed(5)}</div>
                          </div>
                        ) : (
                          <div className="text-sm text-amber-700 font-medium">Venue not set — pros can't check in until you set it</div>
                        )}
                      </div>
                      <Button onClick={() => setVenueLocation(ev.id)} size="sm" variant="outline" className="rounded-none border-[#E8E1D5] flex-shrink-0" data-testid={`set-venue-${ev.id}`}>
                        {ev.venue ? 'Update' : 'Set Venue'}
                      </Button>
                    </div>

                    {/* Pro slots */}
                    <div className="space-y-2 mb-3">
                      {(ev.pro_slots || []).length === 0 ? (
                        <p className="text-sm text-text-secondary italic">No professionals assigned yet.</p>
                      ) : (
                        ev.pro_slots.map((slot) => (
                          <div key={slot.slot_id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2 border border-[#E8E1D5] rounded" data-testid={`slot-${slot.slot_id}`}>
                            <div className="min-w-0">
                              <div className="font-medium text-sm">{slot.pro_name} <span className="text-xs text-text-secondary capitalize">• {slot.category}</span></div>
                              <div className="text-xs text-text-secondary">Expected: {slot.expected_check_in}</div>
                              {slot.checked_in_at && (
                                <div className="text-[10px] text-emerald-700">Checked in via {slot.marked_by} ({slot.check_in_distance_m}m)</div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 flex-wrap flex-shrink-0">
                              <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${STATUS_BADGE[slot.status] || 'bg-gray-100 text-gray-700'}`}>{slot.status}</span>
                              {slot.status === 'pending' && (
                                <>
                                  <Button onClick={() => markAttendance(ev.id, slot.slot_id, 'present')} size="sm" className="h-7 px-2 rounded-none bg-green-600 hover:bg-green-700 text-white" data-testid={`mark-present-${slot.slot_id}`}>
                                    <CheckCircle size={12} className="mr-1" /> Present
                                  </Button>
                                  <Button onClick={() => markAttendance(ev.id, slot.slot_id, 'absent')} size="sm" className="h-7 px-2 rounded-none bg-red-500 hover:bg-red-600 text-white" data-testid={`mark-absent-${slot.slot_id}`}>
                                    <XCircle size={12} className="mr-1" /> Absent
                                  </Button>
                                </>
                              )}
                              {slot.status === 'present' && (
                                <Button onClick={() => markAttendance(ev.id, slot.slot_id, 'absent')} size="sm" variant="outline" className="h-7 px-2 rounded-none border-red-300 text-red-500" data-testid={`mark-absent-${slot.slot_id}`}>
                                  Mark Absent
                                </Button>
                              )}
                              <Button onClick={() => removeSlot(ev.id, slot.slot_id)} size="sm" variant="ghost" className="h-7 px-2" data-testid={`remove-slot-${slot.slot_id}`}>
                                <Trash size={12} />
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button onClick={() => assignPro(ev.id)} variant="outline" size="sm" className="rounded-none border-[#E8E1D5]" data-testid={`assign-pro-${ev.id}`}>
                        <UserPlus size={14} className="mr-1" /> Assign Professional
                      </Button>
                      {ev.booking_id && ev.status !== 'completed' && (
                        <Button
                          onClick={() => markBookingComplete(ev.booking_id)}
                          size="sm"
                          className="rounded-none bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`mark-complete-${ev.id}`}
                        >
                          <CheckCircle size={14} className="mr-1" /> Mark Event Complete
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <div className="mt-8">
          <CaptainPackageBookingsCard />
        </div>

        <div className="mt-8">
          <PendingReviewsCard testId="captain-pending-reviews" emptyLabel="Once you mark events complete, you'll see vendors here to rate." />
        </div>
      </div>
    </div>
  );
};

export default CaptainDashboard;
